import { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "./layoutChrome";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Confronta from "./Confronta";
import ConfrontaNominas from "./ConfrontaNominas";
import ConfrontaCriteriosAcordeon from "@/components/ConfrontaCriteriosAcordeon";
import ConfrontaXmlPendingBanner from "@/components/confronta/ConfrontaXmlPendingBanner";
import ConfrontaRunProgress from "@/components/confronta/ConfrontaRunProgress";
import { subscribeConfrontaExportProgress } from "@/lib/confrontaExportProgressBus";
import { GitCompare, TrendingDown, UsersRound, Info } from "lucide-react";

export default function ConfrontaCfdiHub({ embedded = false }) {
  const [openSections, setOpenSections] = useState([]);
  const [searchParams] = useSearchParams();
  const [exportProgress, setExportProgress] = useState(null);

  useEffect(() => {
    const o = (searchParams.get("open") || "").toLowerCase();
    if (o === "ingresos" || o === "egresos" || o === "nominas") {
      setOpenSections((prev) => (prev.includes(o) ? prev : [...prev, o]));
    }
  }, [searchParams]);

  useEffect(
    () =>
      subscribeConfrontaExportProgress((detail) => {
        // Pintar de inmediato (el dispatch viene de otro árbol/acordeón).
        flushSync(() => setExportProgress(detail));
      }),
    [],
  );

  const body = (
    <div className={embedded ? "space-y-4" : "w-full max-w-[min(100%,1920px)] mx-auto px-4 py-6 sm:px-6 lg:px-8"}>
        {exportProgress ? (
          <ConfrontaRunProgress
            progress={exportProgress}
            testId="confronta-export-progress"
          />
        ) : null}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 items-start"
          data-testid="confronta-hub-info-row"
        >
          <Accordion type="single" collapsible className="rounded-sm border border-sky-200 bg-sky-50/90 h-full">
            <AccordionItem value="requisitos-confronta" className="border-0">
              <AccordionTrigger
                compact
                className="text-slate-800 leading-snug hover:no-underline [&[data-state=open]]:pb-1"
                data-testid="confronta-hub-requisitos-notice-trigger"
              >
                <span className="flex items-center gap-2 text-left">
                  <Info className="w-4 h-4 shrink-0 text-sky-700" strokeWidth={1.75} aria-hidden />
                  <span className="font-semibold text-slate-900">
                    Requisitos previos para una confronta fiable
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent
                className="px-4 pb-3 pt-0 text-sm text-slate-800 leading-relaxed"
                data-testid="confronta-hub-requisitos-notice"
                data-requisitos-version="4"
              >
                <div className="pl-8 min-w-0 flex-1" role="note">
                  <ol className="list-decimal pl-5 space-y-2 m-0">
                    <li>
                      Tenga cargados en el sistema los <strong>CFDI descargados del SAT de forma completa</strong> (XML
                      integrados y alineados al periodo y RFC que vaya a contrastar).
                    </li>
                    <li>
                      Cargue los <strong>archivos de Excel</strong> con la información de conciliación contable, o los
                      reportes de facturas pagadas y cobradas que utilice como insumo, <strong>cumpliendo el formato y
                      columnas</strong> que exige cada módulo de confronta para los cálculos.
                    </li>
                    <li>
                      Si complementa el proceso con la <strong>conciliación manual de CFDI</strong>, verifique que los
                      comprobantes estén <strong>completos y consistentes</strong> con dichos insumos, de modo que los cruces
                      y totales puedan obtenerse correctamente.
                    </li>
                  </ol>
                  <div
                    className="mt-3 rounded-sm border border-sky-300/80 bg-white/70 px-3 py-2.5 text-sm text-slate-800"
                    data-testid="confronta-hub-requisitos-paso-4"
                  >
                    <p className="m-0">
                      <span className="font-semibold text-slate-900">4.</span> Al terminar de revisar los puntos anteriores,
                      pulse <strong>Ejecutar confronta</strong> en la sección correspondiente (Ingresos, Egresos o Nóminas) para
                      generar el cruce con el periodo y cliente seleccionados.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <ConfrontaCriteriosAcordeon />
        </div>
        <ConfrontaXmlPendingBanner />
        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={setOpenSections}
          className="rounded-sm border border-slate-200 bg-white shadow-sm"
        >
          <AccordionItem value="ingresos" className="border-slate-200 px-0">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <GitCompare className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Confronta Ingresos
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4 min-w-0">
                <Confronta direccion="ingreso" embedMode />
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="egresos" className="border-slate-200 px-0">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Confronta Egresos
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4 min-w-0">
                <Confronta direccion="egreso" embedMode />
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="nominas" className="border-slate-200 px-0 border-b-0">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <UsersRound className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Confronta Nóminas
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4 min-w-0">
                <ConfrontaNominas embedMode />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
    </div>
  );

  if (embedded) return body;

  return (
    <>
      <PageHeader
        title="Confronta CFDI SAT"
        subtitle="Cruce sistema vs Excel para ingresos, egresos y nóminas. Cada sección es independiente."
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, "Confronta CFDI SAT"]}
      />
      {body}
    </>
  );
}
